import { useContext, useEffect, useState } from 'react'
import { Box, Button, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import gaiaTheme from '../../../root-components/theme'
import { ShallowFormDisplay } from '../../presentation-components/form-field-components/general/shallow-form-display'
import moment from 'moment'
import { Add, DeleteOutline, EditOutlined, InfoOutlined } from '@mui/icons-material'
import { IProductComponentFull } from '../../../shared/services/product-component/product-component.model'
import ProductComponentService from '../../../shared/services/product-component/product-component.service'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { BackButtonLink } from '../../presentation-components/display-elements-components/back-button-link'
import { TooltipHover } from '../../presentation-components/miscellaneous-components/tooltip-hover'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { ProductInputFilter } from '../../presentation-components/product-components/product-input-filter'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'
import { getCategoryDisplayName } from '../../../shared/utils/category-utils'

export const ProductComponentDetailPage = () => {

  const productComponentService = new ProductComponentService();
  const { id } = useParams();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [selected, setSelected] = useState<string[]>([]);
  const [isAddingConversion, setIsAddingConversion] = useState<boolean>(false);
  const [productComponent, setProductComponent] = useState<IProductComponentFull>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [backLink, setBackLink] = useState<string>('/products?tab=1')

  useEffect(() => {
    getComponentDetails()

    if (searchParams.has('back')) {
      setBackLink(`/products/${searchParams.get('back')}`)
    }
  }, [])

  const getComponentDetails = () => {
    if (id) {
      productComponentService.getOne(id)
        .then(response => {
          setProductComponent(response)
        })
    }
  }

  const rows = productComponent?.linkedConversions.map((conversion, index) => {
    return {
      id: index,
      action: (
        <TableActionButtons
          id={productComponent.id!}
          includeDelete={true}
          onDelete={deleteClickHandler}
        />
      ),
      category: getCategoryDisplayName(conversion.conversion.category),
      description: `${conversion.conversion.detailLevelTwo}${conversion.conversion.detailLevelThree ? ', ' : ''}${conversion.conversion.detailLevelThree}${conversion.conversion.detailLevelFour ? ', ' : ''}${conversion.conversion.detailLevelFour}${conversion.conversion.detailLevelFive ? ', ' : ''}${conversion.conversion.detailLevelFive}`,
      unitsConsumed: conversion.unitsConsumed,
      measurementUnit: conversion.conversion.measurementUnit.name,
      perXProductComponents: conversion.perXProductComponents,
    }
  });

  const deleteClickHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you would like to remove this emission from this component?`,
      buttonCopy: 'Continue',
      hideCancelButton: false
    })
      .then((response) => {
        if (response) {
          productComponentService.removeConversion(productComponent!.id!, id)
            .then(response => {
              getComponentDetails();
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
            })

        }
      })
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Remove', sortable: false },
    { id: 'category', numeric: false, label: 'Category', sortable: true },
    { id: 'description', numeric: false, label: 'Description', sortable: false },
    { id: 'unitsConsumed', numeric: true, label: 'Units consumed', sortable: true },
    { id: 'measurementUnit', numeric: false, label: 'Measurement unit', sortable: true },
    { id: 'perXProductComponents', numeric: true, label: 'Per X product components', sortable: true },
  ];

  const cancelAddingConversion = () => {
    setIsAddingConversion(false)
  }

  const handleAddConversion = (conversion: any) => {
    productComponentService.addConversion(
      productComponent?.id!,
      conversion.conversionId,
      conversion.unitsConsumed!,
      conversion.perXProductComponents!,
      conversion.groupId,
      conversion.locationId)
      .then(() => {
        cancelAddingConversion();
        getComponentDetails()
      })
  }

  return (
    <>
      <Box sx={{ pb: isMobileMode ? 3 : 6, pl: isMobileMode ? 3 : 6, pr: isMobileMode ? 3 : 6, pt: isMobileMode ? 3 : 5, mt: isMobileMode ? 5 : 0, }}>
        <Box sx={{ mb: 1 }}>
          <BackButtonLink buttonText={searchParams.has('back') ? 'Back to product' : 'Back to products'} link={backLink} />
        </Box>
        {productComponent && <>
          <Typography variant='h4' sx={{ color: 'text.primary', mb: 4 }}> Component Details </Typography>
          <Box sx={{ mb: 8 }}>
            <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mb: 1 }}>
              <Button variant='text' sx={{ mr: 2 }} component={Link} to={`/products/component/edit/${id}`}>
                <EditOutlined sx={{ mr: 0.5 }} />
                <Typography>Edit</Typography>
              </Button>
            </Stack>
            <ShallowFormDisplay name='Component Name' value={productComponent.name} isTopRow={true}></ShallowFormDisplay>
            <ShallowFormDisplay name='Description' value={productComponent.description}></ShallowFormDisplay>
            <ShallowFormDisplay name='Supplier' value={productComponent.contactSupplier?.name || 'Unknown'}></ShallowFormDisplay>
            <ShallowFormDisplay name='Configuration' value={productComponent.componentConfiguration}></ShallowFormDisplay>
            <ShallowFormDisplay
              name='kg CO2e per unit'
              value={productComponent.kgCarbonEmission || 'N/A'}
              tooltipElement={<TooltipHover
                title={'Additional emissions'}
                backgroundColor='white'
                text={'The emission figure here will be added to any emission calculated from the listed specific emissions below. You can use this to account for any emissions which you cannot assign below.'}
                placement='bottom'>
                <InfoOutlined sx={{ ml: 1, color: 'text.secondary' }} />
              </TooltipHover>}></ShallowFormDisplay>
            <ShallowFormDisplay name='Created on' value={moment(productComponent.createdAt).format('DD/MM/YYYY')} isBottomRow={true}></ShallowFormDisplay>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}> Emissions </Typography>
              {!isAddingConversion && <Button variant='text' onClick={() => { setIsAddingConversion(true) }}>
                <Add sx={{ color: 'primary.main' }} />
                <Typography sx={{ color: 'primary.main', display: 'inline' }}>Add New Emission</Typography>
              </Button>}
            </Stack>
            {isAddingConversion && <>
              <Typography variant='body1' sx={{ color: 'text.primary', mt: 2 }}> Please select an emission to add: </Typography>
              <ProductInputFilter onCancel={cancelAddingConversion} onFoundConversion={handleAddConversion} />
              {/* <ConversionFinder onFoundConversion={handleAddConversion} onCancel={cancelAddingConversion} allowPerXProductComponents={true}></ConversionFinder> */}
            </>}
          </Box>
          {
            productComponent &&
            <TableDisplay
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              selected={selected}
              selectByIcon={true}
              setSelected={setSelected} />
          }
        </>}
      </Box>
    </>
  )
}